/**
 * jsonMergeUtil_Conditional.ts - Conditional block handling
 * Port of C# JsonMergeUtil_Conditional.cs
 * Pure ES Module version
 */

import { TemplateOperation } from '../../model/index.js';
import { TemplateOperationKind } from '../../model/index.js';
import { indexOfCaseInsensitive } from './jsonMergeUtil_Replace.js';

// [Dafny Verified] Properties H1-H6 — Verification/dafny/src/HandleConditional.dfy
/**
 * Helper: Handle conditional blocks like {{?Selected}}...{{/Selected}} and {{^Selected}}...{{/Selected}}
 * Positive blocks ({{?key}}): Show content when condition is true
 * Negative blocks ({{^key}}): Show content when condition is false (inverted)
 */
export function handleConditional(
    input: string,
    key: string,
    condition: boolean,
    templateKey: string = '',
    currentTemplateName: string = '',
    operations: TemplateOperation[] | null = null
): string {
    // Check if there's actually a conditional block for this key before processing
    // This matches C#/Rust's behavior - only log operations if a block exists
    const posBlock = '{{?' + key + '}}';
    const negBlock = '{{^' + key + '}}';
    const hasConditional = indexOfCaseInsensitive(input, posBlock) >= 0
        || indexOfCaseInsensitive(input, negBlock) >= 0;
    // Fast path: with no {{?key}}/{{^key}} block present, every loop below would
    // scan the full input and find nothing (loops enter only on a start tag).
    // The tail operation logging is already gated on hasConditional, so this is
    // behavior-identical.
    if (!hasConditional) return input;

    // FIRST: Handle paired positive/negative blocks together to properly handle whitespace between them
    // Pattern: {{?key}}...{{/key}}WHITESPACE{{^key}}...{{/key}}
    // When condition is TRUE: keep positive content only (no whitespace between, no negative)
    // When condition is FALSE: keep negative content only (no whitespace between, no positive)
    const posStart = '{{?' + key + '}}';
    const posEnd = '{{/' + key + '}}';
    const negStart = '{{^' + key + '}}';
    const negEnd = '{{/' + key + '}}';

    let result = input;
    let posStartIdx = indexOfCaseInsensitive(result, posStart);
    while (posStartIdx >= 0) {
        let posEndIdx = indexOfCaseInsensitive(result.substring(posStartIdx + posStart.length), posEnd);
        if (posEndIdx < 0) break;
        posEndIdx += posStartIdx + posStart.length; // Convert to absolute index

        const afterPosEnd = posEndIdx + posEnd.length;

        // Check if there's a negative block immediately following (within whitespace only)
        let negStartIdx = indexOfCaseInsensitive(result.substring(afterPosEnd), negStart);
        if (negStartIdx >= 0 && negStartIdx <= 100) {
            negStartIdx += afterPosEnd; // Convert to absolute index
            const between = result.substring(afterPosEnd, negStartIdx);
            if (between.trim() === '') {
                // Found paired blocks - find the end of negative block
                let negEndIdx = indexOfCaseInsensitive(result.substring(negStartIdx + negStart.length), negEnd);
                if (negEndIdx >= 0) {
                    negEndIdx += negStartIdx + negStart.length; // Convert to absolute index

                    // Extract contents
                    const posContent = result.substring(posStartIdx + posStart.length, posEndIdx);
                    const negContent = result.substring(negStartIdx + negStart.length, negEndIdx);

                    // Determine replacement based on condition
                    let replacement: string;
                    if (condition) {
                        // TRUE: keep positive content only, no whitespace between, no negative
                        replacement = posContent;
                    } else {
                        // FALSE: keep negative content only, no whitespace between, no positive
                        replacement = negContent;
                    }

                    // Replace the entire paired block (from posStart to negEnd)
                    const fullEnd = negEndIdx + negEnd.length;
                    result = result.substring(0, posStartIdx) + replacement + result.substring(fullEnd);

                    // Continue searching from where we left off
                    posStartIdx = indexOfCaseInsensitive(result, posStart);
                    continue;
                }
            }
        }

        // No paired block found, advance past this occurrence and continue searching
        posStartIdx = indexOfCaseInsensitive(result, posStart, afterPosEnd);
    }

    // Handle remaining unpaired positive conditionals: {{?key}}...{{/key}}
    // Support spaces inside block tags, e.g. {{?Selected}} ... {{ /Selected}}
    let condStart = '{{?' + key + '}}';
    let condEnd = '{{ /' + key + '}}';
    let startIdx = indexOfCaseInsensitive(result, condStart);
    let endIdx = startIdx !== -1 ? indexOfCaseInsensitive(result, condEnd, startIdx + condStart.length) : -1;

    while (startIdx !== -1 && endIdx !== -1) {
        const content = result.substring(startIdx + condStart.length, endIdx);
        if (condition) {
            // Keep content
            result = result.substring(0, startIdx) + content + result.substring(endIdx + condEnd.length);
        } else {
            // Remove block content only - preserve surrounding whitespace for fidelity
            result = result.substring(0, startIdx) + result.substring(endIdx + condEnd.length);
        }
        startIdx = indexOfCaseInsensitive(result, condStart);
        endIdx = startIdx !== -1 ? indexOfCaseInsensitive(result, condEnd, startIdx + condStart.length) : -1;
    }

    // Also handle without space: {{/key}}
    condEnd = '{{/' + key + '}}';
    startIdx = indexOfCaseInsensitive(result, condStart);
    endIdx = startIdx !== -1 ? indexOfCaseInsensitive(result, condEnd, startIdx + condStart.length) : -1;

    while (startIdx !== -1 && endIdx !== -1) {
        const content = result.substring(startIdx + condStart.length, endIdx);
        if (condition) {
            result = result.substring(0, startIdx) + content + result.substring(endIdx + condEnd.length);
        } else {
            // Remove block content only - preserve surrounding whitespace for fidelity
            result = result.substring(0, startIdx) + result.substring(endIdx + condEnd.length);
        }
        startIdx = indexOfCaseInsensitive(result, condStart);
        endIdx = startIdx !== -1 ? indexOfCaseInsensitive(result, condEnd, startIdx + condStart.length) : -1;
    }

    // Handle negative conditionals: {{^key}}...{{/key}}
    // Show content when condition is FALSE (inverted from positive blocks)
    let negCondStart = '{{^' + key + '}}';
    let negCondEnd = '{{ /' + key + '}}';
    startIdx = indexOfCaseInsensitive(result, negCondStart);
    endIdx = startIdx !== -1 ? indexOfCaseInsensitive(result, negCondEnd, startIdx + negCondStart.length) : -1;

    while (startIdx !== -1 && endIdx !== -1) {
        const content = result.substring(startIdx + negCondStart.length, endIdx);
        if (!condition) { // Inverted - keep content when false
            result = result.substring(0, startIdx) + content + result.substring(endIdx + negCondEnd.length);
        } else {
            // Remove block content only - preserve surrounding whitespace for fidelity
            result = result.substring(0, startIdx) + result.substring(endIdx + negCondEnd.length);
        }
        startIdx = indexOfCaseInsensitive(result, negCondStart);
        endIdx = startIdx !== -1 ? indexOfCaseInsensitive(result, negCondEnd, startIdx + negCondStart.length) : -1;
    }

    // Also handle without space: {{/key}}
    negCondEnd = '{{/' + key + '}}';
    startIdx = indexOfCaseInsensitive(result, negCondStart);
    endIdx = startIdx !== -1 ? indexOfCaseInsensitive(result, negCondEnd, startIdx + negCondStart.length) : -1;

    while (startIdx !== -1 && endIdx !== -1) {
        const content = result.substring(startIdx + negCondStart.length, endIdx);
        if (!condition) { // Inverted
            result = result.substring(0, startIdx) + content + result.substring(endIdx + negCondEnd.length);
        } else {
            // Remove block content only - preserve surrounding whitespace for fidelity
            result = result.substring(0, startIdx) + result.substring(endIdx + negCondEnd.length);
        }
        startIdx = indexOfCaseInsensitive(result, negCondStart);
        endIdx = startIdx !== -1 ? indexOfCaseInsensitive(result, negCondEnd, startIdx + negCondStart.length) : -1;
    }

    // Log ConditionalBlock operation to match NormalJson format
    // Only log if a conditional block was actually found (matches C#/Rust behavior)
    if (hasConditional && operations !== null && templateKey !== '' && currentTemplateName !== '') {
        // Always use negative block tag for the target (like NormalJson does)
        const targetTag = '{{^' + key + '}}';
        const conditionText = condition ? 'True' : 'False';

        operations.push(new TemplateOperation({
            templateName: templateKey,
            kind: TemplateOperationKind.ConditionalBlock,
            target: targetTag,
            replacedWith: `${currentTemplateName}_${key} (${conditionText})`,
            operations: []
        }));
    }

    return result;
}
